import { Box, Image, Link, Radio, RadioGroup, Text, useDisclosure } from '@chakra-ui/react';
import { useContext } from 'react';
import { IoHappyOutline, IoGiftOutline } from 'react-icons/io5';
import BondaDetailModal from './BondaDetailModal';
import { FiDollarSign } from 'react-icons/fi';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import MerchantContext from '../contexts/MerchantContext';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import palommaPlusLogo from '../assets/palomma-plus-logo.png';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import { PALOMMA_PLUS } from '@/data/subscriptions';

const SELECTED_INSET_BORDER = 'inset 0 0 0 2px rgba(22,18,54,0.6)';

const SubscriptionOfferCard = (): JSX.Element | null => {
  const { showSubscriptionOffer, selectedSubscription, selectSubscription, offer } = useContext(SubscriptionsContext);
  const { invoiceSelectionLocked } = useContext(InvoicesToPayContext);
  const { merchant } = useContext(MerchantContext);
  const { isXXLargeScreen, isSmallScreen } = useBreakpointsContext();
  const formatCurrency = useCurrencyFormatter();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!merchant) return <></>;

  if (!showSubscriptionOffer || !offer) return null;

  const { puntosColombia, rewardInterval, price } = offer;
  const { rewardAmount } = PALOMMA_PLUS;

  const px = isXXLargeScreen ? 3 : 2.5;
  const radioValue = selectedSubscription === true ? 'yes' : selectedSubscription === false ? 'no' : '';

  const cardBorderProps = {
    border: '1px solid',
    borderColor: '#DFDEE2',
    borderRadius: '8px',
    transition: 'border-color 0.2s',
  };

  return (
    <Box mt={4} width="100%" px={px}>
      <Box
        border="1px solid"
        borderColor="#E7E6EB"
        borderRadius="12px"
        bg="rgba(22,18,54,0.015)"
        p={isSmallScreen ? 3 : 4}
        boxShadow="0 1px 2px rgba(22,18,54,0.04)"
      >
        <RadioGroup
          value={radioValue}
          onChange={(v) => selectSubscription(v === 'yes')}
          isDisabled={invoiceSelectionLocked}
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Yes option */}
            <Box
              as="label"
              display="block"
              cursor={invoiceSelectionLocked ? 'not-allowed' : 'pointer'}
              p={4}
              {...cardBorderProps}
              opacity={invoiceSelectionLocked ? 0.9 : 1}
              bg={invoiceSelectionLocked ? '#FAFAFC' : '#FFFFFF'}
              boxShadow={selectedSubscription === true ? SELECTED_INSET_BORDER : undefined}
              transition="box-shadow 0.2s, border-color 0.2s"
              _hover={
                invoiceSelectionLocked
                  ? undefined
                  : selectedSubscription !== true
                  ? { bg: 'rgba(22,18,54,0.02)', borderColor: 'rentals.primary' }
                  : { borderColor: 'rentals.primary' }
              }
            >
              <Box display="flex" alignItems="flex-start" gap={3}>
                <Radio
                  value="yes"
                  mt={1}
                  colorScheme="purple"
                  flexShrink={0}
                  sx={{
                    _disabled: {
                      opacity: 1,
                      cursor: 'not-allowed',
                    },
                  }}
                />
                <Box flex={1}>
                  <Box
                    display="flex"
                    flexDirection={isSmallScreen ? 'column' : 'row'}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={4}
                  >
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Text fontWeight={600} fontSize="0.95rem">
                          Quiero beneficios por el pago de mi arriendo
                        </Text>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1} mt={2}>
                        {puntosColombia > 0 && (
                          <Box display="flex" alignItems="center" gap={2}>
                            <IoHappyOutline size={16} color="#6B6A7A" />
                            <Text fontSize="0.85rem" color="#6B6A7A">
                              Recibe {puntosColombia} Puntos Colombia por el pago de tu arriendo
                            </Text>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" gap={2}>
                          <FiDollarSign size={16} color="#6B6A7A" />
                          <Text fontSize="0.85rem" color="#6B6A7A">
                            Participa por {formatCurrency(rewardAmount)} para tu próximo arriendo. ¡Cada{' '}
                            {rewardInterval} pagos hay un ganador!
                          </Text>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <IoGiftOutline size={16} color="#6B6A7A" />
                          <Text fontSize="0.85rem" color="#6B6A7A">
                            Accede a 800+ descuentos y beneficios y ahorra hasta el 60% en marcas aliadas.{' '}
                            <Link as="button" color="purple.500" textDecoration="underline" onClick={onOpen}>
                              Ver acá
                            </Link>
                          </Text>
                        </Box>
                      </Box>
                      <Text fontSize="0.7rem" color="#9F9DB0" mt={2}>
                        Al seleccionar, aceptas los{' '}
                        <Link href={PALOMMA_PLUS.termsUrl} isExternal color="purple.500" textDecoration="underline">
                          Términos y Condiciones y Política de Tratamiento de Datos
                        </Link>
                      </Text>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems={isSmallScreen ? 'center' : 'flex-end'}
                      justifyContent={isSmallScreen ? 'center' : 'space-between'}
                      alignSelf={isSmallScreen ? 'center' : 'stretch'}
                      gap={isSmallScreen ? 4 : 0}
                      flexShrink={0}
                    >
                      <Text fontSize="0.9rem">{formatCurrency(price)}/mes</Text>
                      <Box display="flex" flexDirection={isSmallScreen ? 'row' : 'column'} gap={2} alignItems="center">
                        <Image src={palommaPlusLogo} alt="Palomma Plus" maxH="28px" objectFit="contain" />
                        {!merchant?.subscriptionConfig?.cobrandingDisabled && (
                          <>
                            <Box
                              w={isSmallScreen ? '1px' : '100%'}
                              h={isSmallScreen ? '28px' : '1px'}
                              bg="#DFDEE2"
                              flexShrink={0}
                            />
                            <Image src={merchant.logoUrl} alt="Merchant logo" maxH="28px" objectFit="contain" />
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* No option */}
            <Box
              as="label"
              display="block"
              cursor={invoiceSelectionLocked ? 'not-allowed' : 'pointer'}
              p={4}
              {...cardBorderProps}
              opacity={invoiceSelectionLocked ? 0.9 : 1}
              bg={invoiceSelectionLocked ? '#FAFAFC' : '#FFFFFF'}
              boxShadow={selectedSubscription === false ? SELECTED_INSET_BORDER : undefined}
              transition="box-shadow 0.2s, border-color 0.2s"
              _hover={
                invoiceSelectionLocked
                  ? undefined
                  : selectedSubscription !== false
                  ? { bg: 'rgba(22,18,54,0.02)', borderColor: 'rentals.primary' }
                  : { borderColor: 'rentals.primary' }
              }
            >
              <Box display="flex" alignItems="center" gap={3}>
                <Radio
                  value="no"
                  colorScheme="purple"
                  flexShrink={0}
                  sx={{
                    _disabled: {
                      opacity: 1,
                      cursor: 'not-allowed',
                    },
                  }}
                />
                <Text fontWeight={600} fontSize="0.95rem">
                  No quiero beneficios por el pago de mi arriendo
                </Text>
              </Box>
            </Box>
          </Box>
        </RadioGroup>
        {selectedSubscription === null && (
          <Text fontSize="0.8rem" color="#9F9DB0" mt={2} textAlign="center">
            Selecciona una opción para continuar
          </Text>
        )}
      </Box>
      <BondaDetailModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default SubscriptionOfferCard;
